/**
 * Q1 TA-1204 (CLAUDE.md section 10: "Pseudonymize before anything leaves the raw tier" and "Nothing raw ever
 * reaches an agent"; Kabir's typed "go" on 2026-10-11).
 *
 * The mask a customer's own text goes through before it is put into a model request: a phone number becomes
 * `[phone]`, an email `[email]`, a street address `[address]`, and the contact's own known name or handle becomes
 * the contact's pseudonym (`CUST_0417`). It is deterministic code, never a model (section 4).
 *
 * MODEL INPUT ONLY. The stored message, the B3 contact capture (contact_identities, encrypted, owner-only), every
 * deterministic gate that reads the customer's words (the contact ask, the router, the verifier's inbound read) and
 * what the owner is shown all keep the raw text. Masking any of those would break them; masking the request is what
 * section 10 asks.
 *
 * Which way it leans, and why that differs from the capture. contactDetect.ts refuses anything ambiguous, because a
 * capture that over-matches stores a stranger's order number as a phone. Here an over-match costs the model a digit
 * run it did not need, and a miss sends a person's number to a provider, so the mask also takes two phone spellings
 * the capture refuses: `(212)555-0147` and a bare ten-digit NANP run. It keeps the capture's left and right rules,
 * so a price, a path, a longer digit run and a number written as an order or reference (B3 fix round 1, C3) are
 * never masked.
 *
 * Names: no general NER. Only the contact record's own display name and handle are replaced, and only where the
 * text writes them as a name (a single name part capitalised, never a word of the trade: "Ruby" the customer and
 * a ruby ring are the same letters).
 */

import { FILE_TLDS, findContactSpans, isPlausibleNanp, phoneContextRefused } from "./contactDetect";

export const MODEL_MASK = { phone: "[phone]", email: "[email]", address: "[address]" } as const;

/**
 * True when a text carries a mask token or a contact pseudonym. A draft that repeats one would send the customer a
 * placeholder or this system's token for them, so the verifier refuses it (verifier.ts, `model_mask_token`).
 */
export function containsModelMaskToken(text: string): boolean {
  // Review fix round 1: a model that repeats a token rarely repeats it exactly, so any bracket and inner spacing
  // ("[ phone ]", "{email}", "<address>") and any separator in the pseudonym ("CUST-0417", "CUST 0417", "CUST0417").
  return MASK_TOKEN_VARIANT.test(text) || PSEUDONYM_VARIANT.test(text);
}

const MASK_TOKEN_VARIANT = /[[{(<]\s*(?:phone|email|address)\s*[\]})>]/i;
const PSEUDONYM_VARIANT = /(?<![A-Za-z0-9_])CUST[\s_-]?\d+(?![A-Za-z0-9_])/i;

/**
 * TA-1361 (CLAUDE.md section 10: a prompt leak must expose `CUST_0417`, not a person; TA-1223). The token a known
 * name or handle becomes when the identity carries no pseudonym. Every pseudonym is numbered from CUST_0001 (TA-99,
 * contacts_pseudonym_seq starts at 1), so this one is nobody's, and it is the fallback stage 0 already writes for a
 * thread with no token. It is a CUST_ token, so a draft that repeats it is refused like any pseudonym.
 */
export const UNKNOWN_PSEUDONYM = "CUST_0000";

/**
 * What the contact record knows about the person, for name masking. TA-1361: a null or empty pseudonym still masks
 * the known name and handle, to UNKNOWN_PSEUDONYM.
 */
export interface ModelMaskIdentity {
  readonly pseudonym: string | null;
  readonly displayName?: string | null;
  readonly handle?: string | null;
}

interface Span {
  readonly start: number;
  readonly end: number;
  readonly token: string;
}

export function maskForModel(text: string, identity?: ModelMaskIdentity | null): string {
  if (text === "") return text;
  const spans: Span[] = [];
  for (const h of findContactSpans(text)) {
    // Queue 2026-10-12b fix B (B1): an email the capture found keeps a figure glued to its front, as the mask's own
    // plain-email pass does, so the merge below never hands the figure back to the capture's span.
    const start = h.kind === "email" ? emailStart(text, h.start) : h.start;
    spans.push({ start, end: h.end, token: MODEL_MASK[h.kind] });
  }
  spans.push(...plainEmailSpans(text));
  spans.push(...widerPhoneSpans(text));
  spans.push(...obfuscatedEmailSpans(text));
  spans.push(...findAddressSpans(text).map((a) => ({ ...a, token: MODEL_MASK.address })));
  // TA-1361 (section 10): a name with no pseudonym is still a person's name, so it becomes UNKNOWN_PSEUDONYM rather
  // than reaching the model raw. A null identity knows no name and masks none.
  if (identity) spans.push(...nameSpans(text, { ...identity, pseudonym: identity.pseudonym || UNKNOWN_PSEUDONYM }));
  return apply(text, spans);
}

/**
 * Earliest first, longest first at a tie. R2 fix round 1: spans that overlap are merged into one, which takes the
 * earliest span's token and runs to the furthest end. Dropping the later span instead let a shorter span win and
 * left the rest of a street, city and zip in the text ("Apt 4B, 10 West 57th Street" once gave "[address] 57th
 * Street").
 */
function apply(text: string, spans: readonly Span[]): string {
  let out = "";
  let at = 0;
  for (const s of mergeOverlaps(spans)) {
    out += text.slice(at, s.start) + s.token;
    at = s.end;
  }
  return out + text.slice(at);
}

function mergeOverlaps<S extends { readonly start: number; readonly end: number }>(spans: readonly S[]): S[] {
  const sorted = [...spans].sort((a, b) => a.start - b.start || b.end - a.end);
  const out: S[] = [];
  for (const s of sorted) {
    const last = out[out.length - 1];
    if (last !== undefined && s.start < last.end) {
      if (s.end > last.end) out[out.length - 1] = { ...last, end: s.end };
      continue;
    }
    out.push(s);
  }
  return out;
}

// --- plain emails the capture refuses but a mask must not miss (queue 2026-10-12b fix B, B1) ---

/**
 * The redactor's permissive shape (contactDetect.ts EMAIL_SHAPE_SOURCE: any local part, `@`, a dotted domain and a
 * letter top-level domain), read only where a local-part run starts so it stays linear. The capture's validEmail
 * refuses an email followed by `-`, `_` or a letter and a local part over 64 characters; a person's address written
 * that way ("jenna@gmail.com-thanks") is still a person's address, so the mask takes it. The B3 capture is unchanged.
 */
const PLAIN_EMAIL = /(?<![A-Za-z0-9._%+-])[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
/** A local part that is a figure on its own: an amount, a count or a decimal ("500", "10234", "6.5"). */
const FIGURE_LOCAL = /^\d{1,6}(?:\.\d{1,3})?$/;
/**
 * Fix round 2 (N3, TA-1275): a size or carat word just before an all-digit local part ("size 6.5@kay.com"). Only
 * with one of these, or a `$`, a `#` or a thousands comma (FIGURE_LEAD below), is the local part read as a figure;
 * a bare all-digit mailbox ("845123@qq.com") is a person's address and is masked whole (section 10).
 */
const FIGURE_WORD_LEFT = /\b(?:size|sz|carat|carats|karat|karats|ct|cts|kt)\s*:?\s*$/i;
/**
 * The figure at the front of a local part glued after a `$`, a `#` or a thousands comma ("$1,200@", "#10234@"). At
 * most six digits, so a phone number glued after a `#` ("#2125550147@carrier.example") is masked whole.
 */
const FIGURE_LEAD = /^\d{1,6}(?:\.\d{1,3})?(?![\d.])/;

function plainEmailSpans(text: string): Span[] {
  if (!text.includes("@")) return [];
  const out: Span[] = [];
  for (const m of text.matchAll(PLAIN_EMAIL)) {
    const start = m.index ?? 0;
    let end = start + m[0].length;
    const tld = m[0].slice(m[0].lastIndexOf(".") + 1);
    if (FILE_TLDS.has(tld.toLowerCase())) continue;
    if (/\d/.test(text.charAt(end))) {
      // Letters run straight into a digit ("gmail.comVS2"): the top-level domain is a common one at the front of
      // the run, and the rest is the figure after it. With no common one at the front, the boundary is unknown and
      // the text is left as written (section 4: a figure is never masked to close a leak).
      const lower = tld.toLowerCase();
      const known = COMMON_TLD_LIST.filter((t) => lower.startsWith(t)).sort((a, b) => b.length - a.length)[0];
      if (known === undefined) continue;
      end -= tld.length - known.length;
    }
    out.push({ start: emailStart(text, start), end, token: MODEL_MASK.email });
  }
  return out;
}

/**
 * Where an email's mask starts. Section 4 wins where it meets section 10: a local part that is itself a figure
 * after a size or carat word ("size 6.5@kay.com") or begins with the figure a `$`, a `#` or a thousands comma
 * started ("$1,200@site.com", "#10234@kay.com") keeps that figure, and the mask covers the rest of the address. With
 * no such cue an all-digit local part is a mailbox ("845123@qq.com", N3, TA-1275) and a ten-digit one is a phone
 * ("2125550147@carrier.example"), so both are masked whole.
 */
function emailStart(text: string, start: number): number {
  const at = text.indexOf("@", start);
  if (at < 0) return start;
  const local = text.slice(start, at);
  if (FIGURE_LOCAL.test(local) && FIGURE_WORD_LEFT.test(text.slice(Math.max(0, start - 12), start))) return at;
  const prev = text.charAt(start - 1);
  const figureBefore = prev === "$" || prev === "#" || (prev === "," && /\d/.test(text.charAt(start - 2)));
  const lead = figureBefore ? FIGURE_LEAD.exec(local) : null;
  return lead ? start + lead[0].length : start;
}

// --- phones the capture refuses but a mask must not miss ---

const PAREN_NO_SPACE = /\(\d{3}\)\d{3}[\s.-]?\d{4}/g;
const BARE_RUN = /(?<!\d)1?\d{10}(?!\d)/g;

/**
 * R2 item 1 (TA-1223 found, not built). More spellings a person types, mask only (the B3 capture still refuses
 * them): `212 5550147` and `212-5550147` (area code, then seven digits), and any ten-digit phone with an extension
 * glued on (`x12`, `ext12`), whose letter-after-digits the capture's right rule refuses. The same left and right
 * rules apply, so a number written as an order, a report or a price is still never masked.
 */
const AREA_THEN_SEVEN = /(?<!\d)\(?\d{3}\)?[\s.-]\d{7}(?!\d)/g;
const THREE_THREE_FOUR = /(?<!\d)\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}(?!\d)/g;
/**
 * R2 fix round 2: an extension is never masked. Once the number is masked its extension is no person's data, and
 * round 1's extension tail (`#\d{2,5}`, a comma, `extn`) ate order numbers and quantities written after a phone
 * ("212-555-0147 #10234 is my order"). This only lets the right rule see past an extension glued straight onto the
 * number, so "call 2125550147x12" masks the number and leaves "x12". A spaced extension needs nothing: a space
 * already passes the right rule. A glued letter that is no extension ("4402183771x please") still refuses.
 */
const GLUED_EXTENSION = /^(?:x|ext|extn|extension)\.?\d{1,5}(?![A-Za-z0-9])/i;

/** Where the right rule reads from: after a glued extension when there is one, else at the number's end. */
function rightRuleAt(text: string, end: number): number {
  const tail = GLUED_EXTENSION.exec(text.slice(end, end + 16));
  return tail ? end + tail[0].length : end;
}

function widerPhoneSpans(text: string): Span[] {
  const out: Span[] = [];
  for (const m of text.matchAll(AREA_THEN_SEVEN)) {
    const start = m.index ?? 0;
    const end = start + m[0].length;
    if (phoneContextRefused(text, start, rightRuleAt(text, end))) continue;
    if (!isPlausibleNanp(m[0].replace(/\D/g, ""))) continue;
    out.push({ start, end, token: MODEL_MASK.phone });
  }
  for (const m of text.matchAll(THREE_THREE_FOUR)) {
    const start = m.index ?? 0;
    const end = start + m[0].length;
    const ruleAt = rightRuleAt(text, end);
    if (ruleAt === end) continue; // nothing glued on: the capture's own span already covers it
    if (phoneContextRefused(text, start, ruleAt)) continue;
    if (!isPlausibleNanp(m[0].replace(/\D/g, ""))) continue;
    out.push({ start, end, token: MODEL_MASK.phone });
  }
  for (const m of text.matchAll(PAREN_NO_SPACE)) {
    const start = m.index ?? 0;
    const end = start + m[0].length;
    if (phoneContextRefused(text, start, rightRuleAt(text, end))) continue;
    if (!isPlausibleNanp(m[0].replace(/\D/g, ""))) continue;
    out.push({ start, end, token: MODEL_MASK.phone });
  }
  for (const m of text.matchAll(BARE_RUN)) {
    const start = m.index ?? 0;
    const end = start + m[0].length;
    if (phoneContextRefused(text, start, rightRuleAt(text, end))) continue;
    const national = m[0].length === 11 ? m[0].slice(1) : m[0];
    if (m[0].length === 11 && !m[0].startsWith("1")) continue;
    if (!isPlausibleNanp(national)) continue;
    out.push({ start, end, token: MODEL_MASK.phone });
  }
  return out;
}

// --- obfuscated emails (R2 item 2) ---

/**
 * R2 fix round 1. The left edge refuses a start after a digit, a comma, a dot or a `$`, so a local part never begins
 * inside a figure ("$1,250 at bluenile dot com" once lost its "250"). A bracketed form's local part may hold digits
 * but must hold a letter; the bare and spaced-`@` forms, where "at" or "@" is also prose, need a local part that
 * STARTS with a letter, so "the 2.5 at jamesallen dot com" and "18k at brilliantearth dot com" are never read as one.
 */
// R2 fix round 2: nor after a `/`, so a grade written "D/IF" or "F/VS1" never loses its second half.
const EMAIL_LEFT = String.raw`(?<![A-Za-z0-9._%+,$/-])`;
const LOCAL = String.raw`([A-Za-z0-9._%+-]{1,64})`;
const LOCAL_WORDLIKE = String.raw`([A-Za-z][A-Za-z0-9._%+-]{0,63})`;
const LABEL = String.raw`([A-Za-z][A-Za-z0-9-]{0,62})`;
/** `(at)`, `[at]`, `{at}`, `<at>` and, R2 fix round 1, `(@)`, `[@]`, `{@}`, `<@>`. */
const AT_BRACKET = String.raw`[ \t]{0,3}(?:\(at\)|\[at\]|\{at\}|<at>|\(@\)|\[@\]|\{@\}|<@>)[ \t]{0,3}`;
/** A spelled or bracketed dot: ` dot `, `(dot)`, and, R2 fix round 1, `[.]`, `(.)`, `{.}`, `<.>`. */
const DOT_SPELLED = String.raw`(?:[ \t]{0,3}(?:\(dot\)|\[dot\]|\{dot\}|<dot>|\(\.\)|\[\.\]|\{\.\}|<\.>)[ \t]{0,3}|[ \t]{1,3}dot[ \t]{1,3})`;
const COMMON_TLD_LIST = ["com", "net", "org", "edu", "gov", "co", "io", "me", "us", "uk", "ca", "info", "biz", "ai", "app"];
const COMMON_TLD = new Set(COMMON_TLD_LIST);
/**
 * After a bracketed at, a literal dot with no space (`gmail.com`) is a separator, and a spaced one (`gmail . com`)
 * is one only when a common top-level domain follows it straight away and then the word ends. R2 fix round 2: a
 * spaced dot with any word after it was read as a separator, so "email me (at) work. Thanks" became an address and
 * took the words after it. After a bare at a spaced dot is always a full stop.
 */
const SPACED_DOT_BEFORE_TLD = String.raw`(?:[ \t]{1,3}\.[ \t]{0,3}|\.[ \t]{1,3})(?=(?:${COMMON_TLD_LIST.join("|")})(?![A-Za-z0-9]))`;
const DOT_ANY = String.raw`(?:${DOT_SPELLED}|\.(?![ \t])|${SPACED_DOT_BEFORE_TLD})`;
/** `ruby(at)gmail(dot)com`, `ruby [at] gmail . com`, `ruby(@)gmail(.)com`: a bracketed at is unambiguous. */
const OBFUSCATED_BRACKET = new RegExp(
  String.raw`${EMAIL_LEFT}${LOCAL}${AT_BRACKET}${LABEL}(?:${DOT_ANY}${LABEL}){1,6}(?![A-Za-z0-9-])`,
  "gi",
);
/**
 * `ruby at gmail dot com` and, R2 fix round 1, `ruby at gmail.com` (the literal dot directly between two labels). A
 * bare "at" is a word, so the local part is checked against prose and the top-level domain must be a common one or
 * a two-letter country code.
 */
const OBFUSCATED_BARE = new RegExp(
  String.raw`${EMAIL_LEFT}${LOCAL_WORDLIKE}[ \t]{1,3}at[ \t]{1,3}${LABEL}(?:(?:${DOT_SPELLED}|\.)${LABEL}){1,6}(?![A-Za-z0-9-])`,
  "gi",
);
/** R2 fix round 1: `ruby @ gmail.com`, `ruby @ gmail dot com`. Read like the bare form: "meet @ 3pm" is prose. */
const OBFUSCATED_SPACED_AT = new RegExp(
  String.raw`${EMAIL_LEFT}${LOCAL_WORDLIKE}(?:[ \t]{1,3}@[ \t]{0,3}|@[ \t]{1,3})${LABEL}(?:(?:${DOT_SPELLED}|\.)${LABEL}){1,6}(?![A-Za-z0-9-])`,
  "gi",
);
const DOT_SPLIT = /[ \t]*(?:\(dot\)|\[dot\]|\{dot\}|<dot>|\(\.\)|\[\.\]|\{\.\}|<\.>|\.)[ \t]*|[ \t]+dot[ \t]+/i;

/**
 * R2 fix round 1: words that sit before "at" in a sentence, never as a local part: pronouns, function words and the
 * trade's units ("a 2.5 ct at kay dot com" keeps its "ct").
 */
const NEVER_A_LOCAL = new Set([
  "i", "me", "you", "it", "us", "we", "they", "them", "him", "her", "one", "ones", "this", "that", "these", "those",
  "is", "was", "are", "were", "be", "been", "am", "here", "there", "also", "even", "only", "just", "all", "both",
  "a", "an", "the", "and", "or", "of", "to", "in", "on", "at", "for", "by", "from", "with", "not", "so", "but",
  "ct", "cts", "carat", "carats", "k", "kt", "karat", "karats", "mm", "cm", "g", "gram", "grams", "oz", "pm", "am",
]);
/**
 * R2 fix round 1: words a sentence puts before "at a site" ("she looked at gold dot com", "available at Kay.com").
 * Before a known mail provider they are still read as a local part ("look at gmail dot com" is no sentence a
 * customer writes), so a person whose address starts with one of them is masked at the providers most people use.
 */
const PROSE_BEFORE_AT = new Set([
  "look", "looks", "looked", "looking", "see", "saw", "seen", "seeing", "find", "found", "priced", "price", "prices",
  "listed", "listing", "sold", "sell", "sells", "selling", "bought", "buy", "buying", "available", "cheaper",
  "cheapest", "shop", "shops", "shopped", "shopping", "store", "online", "deal", "deals", "sale", "check", "checked",
  "checking", "browse", "browsed", "browsing", "spotted", "stocked", "offered", "ordered", "order", "purchased",
  "retail", "retails", "cost", "costs", "went", "going", "stopped", "arrived", "arrive", "meet", "meeting", "met",
  "work", "works", "working", "worked", "live", "lives", "living", "lived", "stay", "staying", "stayed", "got", "get",
  "carry", "carries", "carried", "posted", "advertised", "featured", "same", "similar", "ring", "rings", "piece",
]);
const MAIL_PROVIDER = new Set([
  "gmail", "googlemail", "yahoo", "ymail", "outlook", "hotmail", "live", "msn", "icloud", "me", "mac", "aol",
  "protonmail", "proton", "pm", "gmx", "zoho", "yandex", "mail", "comcast", "verizon", "att", "sbcglobal", "cox",
  "charter", "optonline", "earthlink", "fastmail", "hey", "tutanota", "web", "orange", "free", "libero", "qq",
]);
/**
 * R2 fix round 2. A bare or spaced at before a domain written with literal dots ("x at y.com", "x @ y.com") is how a
 * sentence names a site ("a G VS2 at bluenile.com", "Mike at Kay.com"), so it is an address only at one of these
 * personal mail providers. The list is an assumption logged for Kabir (R2 fix round 2 FINDINGS draft).
 */
const LITERAL_DOT_PROVIDER = new Set([
  "gmail", "googlemail", "yahoo", "ymail", "outlook", "hotmail", "live", "msn", "icloud", "me", "mac", "aol",
  "proton", "protonmail", "pm", "gmx", "mail", "zoho", "comcast", "verizon", "att", "sbcglobal", "yandex",
  "fastmail", "hey",
]);
/** A local part before a literal-dot domain: letters, with dots, underscores or hyphens, and never a digit. */
const LETTERS_LOCAL = /^[A-Za-z][A-Za-z._-]*$/;
const SEPARATORS = new RegExp(DOT_SPLIT.source, "gi");
type EmailForm = "bracket" | "bare" | "spacedAt";
/** Where the domain starts: after the first at, which no local part can hold (a local part has no space, no `@`). */
const AT_OF: Record<EmailForm, RegExp> = {
  bracket: /\(at\)|\[at\]|\{at\}|<at>|\(@\)|\[@\]|\{@\}|<@>/i,
  bare: /[ \t]at[ \t]/i,
  spacedAt: /@/,
};

function obfuscatedEmailSpans(text: string): Span[] {
  if (!/at|@/i.test(text)) return [];
  const out: Span[] = [];
  const forms = [[OBFUSCATED_BRACKET, "bracket"], [OBFUSCATED_BARE, "bare"], [OBFUSCATED_SPACED_AT, "spacedAt"]] as const;
  for (const [source, form] of forms) {
    // A fresh regex per call, walked by hand: a match refused below moves on one character, not past the whole
    // match, so "looked at ruby.smith at gmail dot com" still finds the address its refused prefix overlapped.
    const re = new RegExp(source.source, source.flags);
    for (let m = re.exec(text); m !== null; m = re.exec(text)) {
      const start = m.index;
      if (acceptEmail(m[0], m[1]!, m[2]!, form, text.slice(Math.max(0, start - 16), start))) {
        out.push({ start, end: start + m[0].length, token: MODEL_MASK.email });
      } else {
        re.lastIndex = start + 1;
      }
    }
  }
  return out;
}

/** R2 fix round 2: a short number and a space, so the word after it is that number's noun ("3 Rings", "6 weeks"). */
const QUANTITY_LEFT = /(?:^|[^A-Za-z0-9.,$])\$?\d{1,4}(?:[.,]\d{1,3})?[ \t]+$/;

function acceptEmail(match: string, local: string, firstLabel: string, form: EmailForm, before: string): boolean {
  if (!/[A-Za-z]/.test(local)) return false;
  const tld = match.split(DOT_SPLIT).pop()!;
  if (!/^[a-z]{2,24}$/i.test(tld)) return false;
  const atAt = AT_OF[form].exec(match);
  const domain = atAt ? match.slice(atAt.index + atAt[0].length) : match;
  // One case through the domain's last label ("Kay.It's" and "(at) work. Me too" are sentences).
  if (tld !== tld.toLowerCase() && tld !== tld.toUpperCase()) return false;
  const l = local.toLowerCase();
  if (NEVER_A_LOCAL.has(l)) return false;
  const provider = firstLabel.toLowerCase();
  // R2 fix round 2: the word after a short number is a quantity's noun ("3 Rings (at) bluenile dot com"), read as a
  // local part only before a known mail provider.
  if (QUANTITY_LEFT.test(before) && !MAIL_PROVIDER.has(provider)) return false;
  if (form === "bracket") {
    // R2 fix round 2: a token holding a digit before a bracketed at is read as a local part only before a known mail
    // provider, so a grade, a SKU or a size ("SI1 [at] kay.co", "A1234 (at) brilliantearth.com") keeps its figure.
    return !/\d/.test(local) || MAIL_PROVIDER.has(provider);
  }
  // A bare or spaced at is prose until shown otherwise: a common or two-letter top-level domain, and a local part
  // that is not a word of the sentence.
  const t = tld.toLowerCase();
  if (!COMMON_TLD.has(t) && t.length !== 2) return false;
  // R2 fix round 2: a domain written only with literal dots is a site in a sentence unless it is a mail provider,
  // and a token holding a digit is a figure (a grade, a SKU, a size), never a local part there. Before a provider a
  // word of PROSE_BEFORE_AT is still a local part, as round 1 reads it.
  const literalDots = (domain.match(SEPARATORS) ?? []).every((s) => s.trim() === ".");
  if (literalDots) return LITERAL_DOT_PROVIDER.has(provider) && LETTERS_LOCAL.test(local);
  // A spelled dot ("x at y dot com") is how a person hides an address, so round 1's reading stands, except that a
  // token holding a digit is read as a local part only before a known mail provider ("SKU A1234 at kay dot com").
  if (/\d/.test(local) && !MAIL_PROVIDER.has(provider)) return false;
  return !PROSE_BEFORE_AT.has(l) || MAIL_PROVIDER.has(provider);
}

// --- street addresses ---

/** Street types read in any case: no jewelry message uses these words after a number and a name. */
const STRONG_SUFFIX = new Set([
  "st", "street", "ave", "av", "avenue", "rd", "road", "blvd", "boulevard", "dr", "drive", "ln", "lane",
  "pkwy", "parkway", "hwy", "highway", "ter", "terrace",
]);
/** Street types that are also everyday words: an address only after a capitalised street name ("12 Oak Ct"). */
const WEAK_SUFFIX = new Set(["ct", "court", "way", "pl", "place", "sq", "square", "cir", "circle", "plaza", "trl", "trail"]);
const DIRECTION = new Set(["n", "s", "e", "w", "ne", "nw", "se", "sw", "north", "south", "east", "west"]);

/**
 * Words that sit between a number and a street-type word in a jewelry message without being a street: units of
 * time, distance and weight, the trade's own words, and function words. One of them in the name means not an
 * address ("a 2 hour drive", "2 tone court band", "2 by the way").
 */
const NOT_A_STREET_NAME = new Set([
  "a", "an", "the", "by", "of", "to", "in", "on", "at", "for", "and", "or", "my", "your", "our", "her", "his", "their",
  "more", "less", "other", "same", "few", "times", "x",
  "hour", "hours", "hr", "hrs", "minute", "minutes", "min", "mins", "second", "seconds", "day", "days", "week", "weeks",
  "month", "months", "year", "years", "mile", "miles", "mi", "km", "block", "blocks",
  "ct", "cts", "carat", "carats", "karat", "karats", "kt", "k", "mm", "cm", "inch", "inches", "gram", "grams", "g",
  "tone", "stone", "stones", "ring", "rings", "band", "bands", "piece", "pieces", "row", "rows", "prong", "prongs",
  "gold", "white", "yellow", "rose", "platinum", "silver",
  // review fix round 1: a time of day ("at 4 pm Lexington Ave store")
  "am", "pm", "noon", "oclock", "o'clock", "ish",
]);

/** Review fix round 1: a number written after a month is a day of it ("Dec 3 Park Ave gala"), never a house number. */
const MONTH_LEFT =
  /\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec|january|february|march|april|june|july|august|september|october|november|december)\.?\s+$/i;

const HOUSE_NUMBER = /(?<![A-Za-z0-9$.,:#/@_+-])(\d{1,6})([A-JL-Za-jl-z])?(?=\s)/g;
const NEXT_WORD = /^(\s+)([A-Za-z][A-Za-z'-]*\.?|\d{1,3}(?:st|nd|rd|th)\b)/i;
/**
 * R2 fix round 1: what a unit is written as. Digits with at most one letter ("4B", "12", "300") or a single letter
 * ("B"). A word is never a unit, so "the unit price 1250 USD" and "Suite deal 2 Carat Oval" are not addresses.
 */
const UNIT_ID = String.raw`(?:\d{1,5}[A-Za-z]?|[A-Za-z])(?![A-Za-z0-9])`;
const UNIT_TAIL = new RegExp(
  String.raw`^\s*,?\s*(?:(?:apt|apartment|suite|ste|unit|fl|floor|rm|room)\.?\s*#?\s*${UNIT_ID}|#\s*${UNIT_ID})`,
  "i",
);
const POST_DIRECTION = /^\s+(?:N|S|E|W|NE|NW|SE|SW)\b\.?/;
/**
 * R2 fix round 1: a city written with no comma before it goes too ("350 Park Ave New York, NY 10022" left the city),
 * but only with its zip, so "350 Park Ave Thursday, OK" keeps the words after the street.
 */
const CITY_STATE_ZIP =
  /^(?:\s*,\s*[A-Z][A-Za-z.' ]{1,30}?\s*,\s*[A-Z]{2}\b(?:\s+\d{5}(?:-\d{4})?\b)?|[ \t]+[A-Z][A-Za-z.' ]{1,30}?\s*,\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?\b)/;
const PO_BOX = /\b(?:p\.?\s?o\.?\s*box|post\s+office\s+box)\s*#?\s*\d{1,8}\b/gi;

/**
 * R2 item 3. A unit, apartment or suite and its number, then a house number and a capitalised named place with no
 * street-type word: "Apt 4B, 10 Hudson Yards", "Unit 12, 1 World Trade Center". The place must be capitalised
 * ("Unit 5, 2 carat diamonds" is not an address) and is at most three words.
 */
// R2 fix round 1: the unit is UNIT_ID, never a word. The match ends at the house number; what follows is read by
// unitFirstEnd, which takes a full street when there is one and checks a named place against the trade's words.
const UNIT_FIRST = new RegExp(
  String.raw`(?<![A-Za-z0-9])(?:[Aa]pt|APT|[Aa]partment|[Uu]nit|UNIT|[Ss]uite|SUITE|[Ss]te|STE)\.?[ \t]{0,2}#?[ \t]{0,2}${UNIT_ID}(?:,[ \t]{0,2}|[ \t]{1,3})(\d{1,6})(?=[ \t])`,
  "g",
);
const PLACE_WORD = /^[ \t]{1,3}([A-Z][A-Za-z'-]*)/;
const MAX_PLACE_WORDS = 3;

/** R2 item 3: "2 Oval Drive studs" is a quantity and a piece, not a street. */
const JEWELRY_NOUN = new Set([
  "stud", "studs", "ring", "rings", "earring", "earrings", "band", "bands", "pendant", "pendants", "necklace", "necklaces",
  "bracelet", "bracelets", "chain", "chains", "hoop", "hoops", "charm", "charms", "cufflink", "cufflinks", "watch", "watches",
  "piece", "pieces", "bangle", "bangles", "brooch", "brooches", "pair", "pairs",
]);
/** The most a quantity of pieces runs to ("2 Oval Drive studs"); a larger number before a street is a house number. */
const MAX_QUANTITY = 10;

/**
 * Queue 2026-10-12b fix B (B2): the trade's cuts, shapes, stones and styles. A street or place named only with these
 * after a number reads as a count of pieces as easily as an address ("12 Diamond Way bands", "Apt 2, 4 Cushion Halo"),
 * and section 4 says the count must survive.
 */
const TRADE_WORD = new Set([
  "round", "oval", "pear", "cushion", "princess", "emerald", "emeralds", "marquise", "radiant", "asscher", "heart",
  "hearts", "baguette", "baguettes", "trillion", "halo", "solitaire", "solitaires", "pave", "bezel", "prong", "eternity",
  "tennis", "cut", "cuts", "diamond", "diamonds", "sapphire", "sapphires", "ruby", "rubies", "pearl", "pearls", "opal",
  "opals", "moissanite", "gem", "gems", "cluster", "signet", "cocktail", "bridal", "vintage", "accent", "accents",
]);
/** B2: money words a figure is written with ("15 Grand", "20 thousand"); the first name word only. */
const MONEY_WORD = new Set(["grand", "thousand", "hundred", "dollar", "dollars", "bucks", "usd"]);
/**
 * B2: words that introduce an address directly before the house number ("ship to", "live at", "address:"). With one,
 * or a unit, city or zip after the street, the number is a house number; without, a number that may be a count is
 * left outside the span and only the street is masked.
 */
// Fix round 2 (N2, TA-1275): "my address is 22 Emerald Way" is cued too.
const ADDRESS_CUE_LEFT = /\b(?:to|at|address(?:\s+is)?|addr)\s*:?\s+$/i;

/**
 * The one street-address detector. contactMask's model-input mask and the archive redactor in packages/pipeline
 * both read this (CLAUDE.md F-16: one shared module). Start inclusive, end exclusive, in no particular order.
 */
export function findAddressSpans(text: string): Array<{ readonly start: number; readonly end: number }> {
  const out: Array<{ start: number; end: number }> = [];
  for (const m of text.matchAll(PO_BOX)) {
    const start = m.index ?? 0;
    out.push({ start, end: start + m[0].length });
  }
  for (const m of text.matchAll(UNIT_FIRST)) {
    const start = m.index ?? 0;
    const end = unitFirstEnd(text, start + m[0].length, Number(m[1]));
    if (end !== null) out.push({ start, end });
  }
  for (const m of text.matchAll(HOUSE_NUMBER)) {
    const start = m.index ?? 0;
    if (MONTH_LEFT.test(text.slice(Math.max(0, start - 12), start))) continue;
    const quantity = m[2] === undefined ? Number(m[1]) : Infinity;
    const cued = ADDRESS_CUE_LEFT.test(text.slice(Math.max(0, start - 16), start));
    const street = streetAfter(text, start + m[0].length, quantity, cued);
    if (street === null) continue;
    // B2: a number that may be a count stays outside the span unless the address is plain from its cue or its tail.
    // Fix round 2 (N2, TA-1275): a street kept only by its cue or tail against the piece rule keeps its number
    // visible, since a piece follows it and the number reads as that piece's count just as well (section 4).
    const strong = (street.tailed || cued) && !street.pieceRead;
    out.push({ start: street.countReading && !strong ? street.nameStart : start, end: street.end });
  }
  // R2 fix round 1: overlapping spans come back merged, so neither caller (the model mask, the archive redactor in
  // packages/pipeline) can let a shorter span win an overlap and leave the rest of the address behind.
  return mergeOverlaps(out);
}

/**
 * After "Apt 4B, 10": a full street when one follows ("West 57th Street, New York, NY 10019"), else a capitalised
 * named place of at most three words ("Hudson Yards") that holds none of the trade's words and is not followed by a
 * piece ("Apt 4, 2 Oval Halo Studs"), then the usual tail. Null when neither.
 */
function unitFirstEnd(text: string, from: number, quantity: number): number | null {
  // A unit marker before the number is the address's own signal, so a street after it takes the number whole.
  const street = streetAfter(text, from, quantity, false);
  if (street !== null) return street.end;
  let at = from;
  let words = 0;
  let allTrade = true;
  for (; words < MAX_PLACE_WORDS; words++) {
    const w = PLACE_WORD.exec(text.slice(at, at + 80));
    if (!w) break;
    const bare = w[1]!.toLowerCase();
    if (NOT_A_STREET_NAME.has(bare) || JEWELRY_NOUN.has(bare)) return null;
    if (!TRADE_WORD.has(bare)) allTrade = false;
    at += w[0].length;
  }
  if (words === 0) return null;
  // B2: a place named only with the trade's words is a piece and its count ("Apt 2, 4 Cushion Halo for 6,500").
  if (allTrade) return null;
  const after = NEXT_WORD.exec(text.slice(at, at + 40));
  if (quantity <= MAX_QUANTITY && after && JEWELRY_NOUN.has(after[2]!.toLowerCase())) return null;
  return tail(text, at);
}

interface StreetRead {
  /** Where the street, with its direction, unit and city tail, ends. */
  readonly end: number;
  /** Where the street's name starts, after the number and its space. */
  readonly nameStart: number;
  /** A unit, a direction or a city after the street: the address is plain from its tail. */
  readonly tailed: boolean;
  /** B2: the number before the street may be a count (a trade-word name, a money word, a piece after it). */
  readonly countReading: boolean;
  /**
   * Fix round 2 (N2, TA-1275): the piece rule read a count and its piece here, and only an address cue or a tail
   * kept the street. The street is masked and the number stays outside the span.
   */
  readonly pieceRead: boolean;
}

/**
 * The street that starts after a house number, or null when what follows is not a street. `cued` is an address cue
 * directly before the number ("ship to", "address is"), read before the piece rule may return null (N2, TA-1275).
 */
function streetAfter(text: string, from: number, quantity: number, cued: boolean): StreetRead | null {
  let at = from;
  let nameStart = from;
  const names: string[] = [];
  for (let i = 0; i < 6; i++) {
    const w = NEXT_WORD.exec(text.slice(at));
    if (!w) return null;
    if (i === 0) nameStart = at + w[1]!.length;
    const word = w[2]!;
    const bare = word.replace(/\.$/, "").toLowerCase();
    const next = at + w[0].length;
    const isSuffix = STRONG_SUFFIX.has(bare) || WEAK_SUFFIX.has(bare);
    if (isSuffix && names.length > 0 && names.some((n) => !DIRECTION.has(n.toLowerCase()))) {
      const weak = !STRONG_SUFFIX.has(bare);
      const capitalised = names.every((n) => /^\d/.test(n) || /^[A-Z]/.test(n));
      if (!weak || capitalised) {
        const after = NEXT_WORD.exec(text.slice(next, next + 40));
        const pieceAfter = after !== null && JEWELRY_NOUN.has(after[2]!.toLowerCase());
        const named = names.filter((n) => !DIRECTION.has(n.toLowerCase()));
        const tradeName = named.every((n) => TRADE_WORD.has(n.toLowerCase()));
        // B2: a piece after a trade-word name is a count and its piece at any quantity ("12 Diamond Way bands").
        // Fix round 2 (N2, TA-1275): unless an address cue or a tail says it is an address ("ship to 40 Pearl St
        // ring size 6"); then the street is masked and the number left visible, never the street left raw.
        const end = tail(text, next);
        const tailed = end > next;
        const pieceRead = pieceAfter && (quantity <= MAX_QUANTITY || tradeName);
        if (pieceRead && !cued && !tailed) return null;
        const countReading =
          Number.isFinite(quantity) && (pieceAfter || tradeName || MONEY_WORD.has(named[0]!.toLowerCase()));
        return { end, nameStart, tailed, countReading, pieceRead };
      }
    }
    if (NOT_A_STREET_NAME.has(bare)) return null;
    if (word.endsWith(".") && !DIRECTION.has(bare)) return null;
    names.push(word.replace(/\.$/, ""));
    at = next;
  }
  return null;
}

function tail(text: string, from: number): number {
  let at = from;
  const dir = POST_DIRECTION.exec(text.slice(at));
  if (dir) at += dir[0].length;
  const unit = UNIT_TAIL.exec(text.slice(at));
  if (unit) at += unit[0].length;
  const city = CITY_STATE_ZIP.exec(text.slice(at));
  if (city) at += city[0].length;
  return at;
}

// --- the contact's own name and handle ---

/**
 * Name parts that are also the trade's words or everyday words. A part on this list is masked only as part of the
 * whole display name, never alone.
 */
const NAME_PART_VOCABULARY = new Set([
  "ruby", "pearl", "jade", "rose", "amber", "opal", "diamond", "crystal", "emerald", "sapphire", "garnet", "topaz",
  "onyx", "coral", "ivory", "gold", "golden", "silver", "sterling", "platinum", "stone", "jewel", "gem", "ring",
  "band", "chain", "star", "heart", "pear", "oval", "halo", "cushion", "princess", "marquise", "baguette",
  "grace", "hope", "faith", "joy", "love", "honey", "cherry", "olive", "violet", "iris", "lily", "daisy", "ivy",
  "angel", "summer", "april", "may", "june", "august", "will", "mark", "bill", "art", "rich", "sunny", "chase",
  "page", "hunter", "brown", "white", "black", "green", "gray", "grey", "king", "young", "long", "best",
  // review fix round 1: the trade's words a display name is made of ("Tennis Lover", "Emerald Cut", "Eternity")
  "cut", "tennis", "eternity", "solitaire", "bezel", "pave", "prong", "halo", "setting", "bracelet", "necklace",
  "pendant", "earring", "earrings", "stud", "studs", "hoop", "hoops", "bangle", "cuff", "signet", "cocktail",
  "bridal", "vintage", "antique", "estate", "heirloom", "engagement", "wedding", "watch", "watches", "carat",
  "karat", "fan", "fans", "lover", "lovers", "collector", "jewelry", "jewellery", "jeweler", "bling", "sparkle",
  // grading labs, and jewelry and watch houses written as one word
  "gia", "igi", "ags", "hrd", "tiffany", "cartier", "rolex", "bulgari", "bvlgari", "chopard", "graff", "omega",
  "patek", "philippe", "audemars", "piguet", "breitling", "hublot", "panerai", "iwc", "longines", "tudor",
  "vacheron", "constantin", "boucheron", "chaumet", "piaget", "mikimoto", "yurman", "winston", "cleef", "arpels",
  "chanel", "dior", "hermes", "gucci", "pandora", "swarovski", "messika", "buccellati", "pomellato", "tacori",
  "verragio", "zales", "jared", "submariner", "daytona", "datejust", "nautilus",
]);

/**
 * Review fix round 1: houses whose name starts with a common first name ("Harry Winston", "David Yurman"). The
 * phrase is the trade's, so a customer called Harry is not masked inside it; "Harry" alone still is.
 */
const HOUSE_PHRASE =
  /(?<![\p{L}\p{N}_])(?:harry\s+winston|david\s+yurman|van\s+cleef(?:\s*(?:&|and)\s*arpels)?|patek\s+philippe|audemars\s+piguet|tag\s+heuer|jaeger[\s-]+le[\s-]?coultre|vacheron\s+constantin|blue\s+nile|tiffany\s*(?:&|and)\s*co)(?![\p{L}\p{N}_])/giu;

const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const LEFT = String.raw`(?<![\p{L}\p{N}_])`;
const RIGHT = String.raw`(?![\p{L}\p{N}_])`;

function nameSpans(text: string, identity: ModelMaskIdentity & { readonly pseudonym: string }): Span[] {
  const out: Span[] = [];
  const token = identity.pseudonym;
  const push = (re: RegExp, accept: (match: string) => boolean = () => true): void => {
    for (const m of text.matchAll(re)) {
      if (!accept(m[0])) continue;
      const start = m.index ?? 0;
      out.push({ start, end: start + m[0].length, token });
    }
  };
  const capitalised = (m: string): boolean => /^\p{Lu}/u.test(m);

  const display = (identity.displayName ?? "").trim().replace(/\s+/g, " ");
  const parts = display === "" ? [] : display.split(" ").filter((p) => p.length > 0);
  if (parts.length >= 2) {
    // Review fix round 2: the whole name is masked in any case ("hi it's jenna marlowe": customers type their own
    // name in lower case). Only a name with a part in the vocabulary needs its capitalised form, so "an emerald
    // cut" is never the customer "Emerald Cut".
    // R2 item 6: a sequence that matches the WHOLE name is a name even when one part is a trade word ("gia rossi";
    // "the gia report" still stays, a single vocabulary part is never masked alone). When EVERY part is a trade
    // word ("Emerald Cut") the sequence is a name only in capitalised form, so "an emerald cut" stays.
    const collides = parts.every((p) => NAME_PART_VOCABULARY.has(p.toLowerCase()));
    const whole = new RegExp(`${LEFT}${parts.map(escapeRe).join(String.raw`\s+`)}${RIGHT}`, "giu");
    push(whole, collides ? (m) => m.split(/\s+/).every(capitalised) : undefined);
  }
  for (const part of parts) {
    if (part.length < 3 || NAME_PART_VOCABULARY.has(part.toLowerCase())) continue;
    push(new RegExp(`${LEFT}${escapeRe(part)}${RIGHT}`, "giu"), capitalised);
  }

  const handle = (identity.handle ?? "").trim().replace(/^@/, "");
  if (handle.length >= 3) {
    const h = escapeRe(handle);
    push(new RegExp(`(?<![\\p{L}\\p{N}_.@])@${h}${RIGHT}`, "giu"));
    // Without its `@`, a handle is a name only when it could not be a word: it has a digit, `_` or `.` in it.
    if (/[\d_.]/.test(handle)) push(new RegExp(`(?<![\\p{L}\\p{N}_.@])${h}${RIGHT}`, "giu"));
  }
  const houses = [...text.matchAll(HOUSE_PHRASE)].map((m) => ({ start: m.index ?? 0, end: (m.index ?? 0) + m[0].length }));
  return out.filter((s) => text.charAt(s.start) === "@" || !houses.some((h) => s.start < h.end && h.start < s.end));
}
